import type {
  ProcedureAuthoringDraft,
  ProcedureItem,
} from "./procedureAuthoring";

const STORAGE_PREFIX = "assetwatch:procedure-ai-draft:";

export type AiProcedureDraftEnvelope = {
  draft: ProcedureAuthoringDraft;
  sourceMaterial: {
    fileName: string | null;
    retained: boolean;
    documentId: number | null;
    documentNumber: string | null;
  };
};

function storageKey(id: string) {
  return `${STORAGE_PREFIX}${id}`;
}

function canonicalDraftId() {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    (character) => {
      const random = Math.floor(Math.random() * 16);
      const value = character === "x" ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    },
  );
}

export function storeAiProcedureDraft(envelope: AiProcedureDraftEnvelope) {
  const id = globalThis.crypto?.randomUUID?.() ?? `ai-${Date.now()}`;
  sessionStorage.setItem(storageKey(id), JSON.stringify(envelope));
  return id;
}

export function readAiProcedureDraft(id: string) {
  const value = sessionStorage.getItem(storageKey(id));
  if (!value) return null;
  try {
    return JSON.parse(value) as AiProcedureDraftEnvelope;
  } catch {
    sessionStorage.removeItem(storageKey(id));
    return null;
  }
}

export function removeAiProcedureDraft(id: string) {
  sessionStorage.removeItem(storageKey(id));
}

function item(
  id: string,
  text: string,
  type: ProcedureItem["type"] = "text",
): ProcedureItem {
  return {
    id,
    label: text,
    description: "",
    type,
    required: true,
    options: type === "choice" ? ["Option 1", "Option 2"] : [],
    unit: "",
  };
}

export function procedureDraftFromBuilder(
  builder: {
    candidates: Array<{
      draft: {
        name: string;
        description: string | null;
        instructions: Array<{ text: string }>;
        tools: Array<{ text: string }>;
        cautions: Array<{ text: string }>;
      } | null;
    }>;
  },
  input: { category: string; title: string; description: string },
): ProcedureAuthoringDraft {
  const generated = builder.candidates.find(
    (candidate) => candidate.draft,
  )?.draft;
  const title = input.title.trim() || generated?.name.trim() || "";
  const description =
    generated?.description?.trim() || input.description.trim();
  const nextId = () => canonicalDraftId();
  const lists = [
    ["Instructions", generated?.instructions ?? []],
    ["Tools", generated?.tools ?? []],
    ["Cautions", generated?.cautions ?? []],
  ] as const;
  const sections = lists
    .filter(([, entries]) => entries.length > 0)
    .map(([sectionTitle, entries]) => ({
      id: nextId(),
      title: sectionTitle,
      items: entries.map((entry) => item(nextId(), entry.text)),
    }));

  return {
    title,
    description,
    category: input.category,
    categories: input.category ? [input.category] : [],
    categoryIds: [],
    owner: "",
    ownerId: null,
    team: "",
    teamId: null,
    groups: [],
    looseSections: sections,
    looseItems: sections.length ? [] : [item(nextId(), "")],
  };
}
